import React, { Component, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';

// eslint-disable-next-line @typescript-eslint/ban-types
type ErrorBoundaryProps = {};

class ErrorBoundary extends Component<
  React.PropsWithChildren<ErrorBoundaryProps>,
  { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }
> {
  state = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack
      }
    });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  goHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      /* eslint-disable no-restricted-syntax -- Error boundary must use inline styles since theme context is unavailable */
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#f3f4f6',
            color: '#000',
            padding: '20px',
            textAlign: 'center',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          <h1 style={{ fontSize: '36px', fontWeight: 'bold' }}>
            Something went wrong.
          </h1>
          <p style={{ fontSize: '18px', marginBottom: '20px' }}>
            We encountered an issue. Please try again later or refresh the page.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={this.handleRefresh}
              type="button"
              style={{
                padding: '10px 24px',
                fontSize: '16px',
                fontWeight: 500,
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
            <button
              onClick={this.goHome}
              type="button"
              style={{
                padding: '10px 24px',
                fontSize: '16px',
                fontWeight: 500,
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#4f46e5',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Home
            </button>
          </div>

          {/* Show error details in development mode */}
          {import.meta.env.DEV && this.state.error && (
            <details
              style={{
                marginTop: '24px',
                padding: '16px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                textAlign: 'left',
                maxWidth: '800px',
                width: '100%'
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: '#dc2626'
                }}
              >
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px',
                  lineHeight: '1.5'
                }}
              >
                <strong>Error:</strong> {(this.state.error as Error).message}
                {'\n\n'}
                <strong>Stack:</strong>
                {'\n'}
                {(this.state.error as Error).stack}
                {this.state.errorInfo && (
                  <>
                    {'\n\n'}
                    <strong>Component Stack:</strong>
                    {'\n'}
                    {(this.state.errorInfo as ErrorInfo).componentStack}
                  </>
                )}
              </pre>
            </details>
          )}
        </div>
      );
    }

    /* eslint-enable no-restricted-syntax */
    return this.props.children;
  }
}

export default ErrorBoundary;
